import { useState } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@predoc/cow';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OutputCategoryFilterState } from '../types';
import {
  getHasActiveOutputCategoryFilter,
  getSelectedOutputCategoryCount,
} from '../utils/outputCategoryFilter';
import { EfaxOutputCategoryFilterList } from './EfaxOutputCategoryFilterList';

interface EfaxOutputCategorySelectorProps {
  value: OutputCategoryFilterState;
  onChange: (categoryFilter: OutputCategoryFilterState) => void;
}

const formatCategoryCountLabel = (count: number) =>
  count === 1 ? '1 category' : `${count} categories`;

const getTriggerLabel = (categoryFilter: OutputCategoryFilterState) => {
  if (!getHasActiveOutputCategoryFilter(categoryFilter)) {
    return 'Any category';
  }

  return formatCategoryCountLabel(
    getSelectedOutputCategoryCount(categoryFilter),
  );
};

export const EfaxOutputCategorySelector = ({
  value,
  onChange,
}: EfaxOutputCategorySelectorProps) => {
  const [open, setOpen] = useState(false);
  const hasActiveFilters = getHasActiveOutputCategoryFilter(value);
  const triggerLabel = getTriggerLabel(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'border-input bg-background h-9 w-full justify-between rounded-md border px-3 font-normal',
            hasActiveFilters && 'border-blue-400 bg-blue-50',
          )}
          aria-label="Select document categories"
        >
          <span
            className={cn(
              'truncate text-sm',
              hasActiveFilters ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {triggerLabel}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        {open && (
          <EfaxOutputCategoryFilterList
            categoryFilter={value}
            onCategoryFilterChange={onChange}
            listClassName="max-h-60"
            autoFocusSearch
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
